import { useCallback, useEffect, useMemo, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { IconButton, styled } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import Popover from '@mui/material/Popover'
import { Button } from '../../../../../../../components/buttons'
import { colorPalette } from '../../../../../../../style/colorPalette'
import { PageConfigForm } from '../PageConfigForm'
import {
  validatePageEntity,
  PageValidationError,
} from '../../../../../../../../domain/entity/version-report/VersionReportVersionEntity/validator'
import {
  toEntityForValidation,
  VersionReportVersionPageFormModel,
} from '../../model/VersionReportVersionPageFormModel'
import { PAGE_TYPE_LABELS, PAGE_TYPE_COLORS } from '../../constants'

type PageConfigDialogProps = {
  open: boolean
  page: VersionReportVersionPageFormModel | null
  snapshotUuid: string
  onApply: (updated: VersionReportVersionPageFormModel) => void
  onCancel: () => void
}

export const PageConfigDialog = ({
  open,
  page,
  snapshotUuid,
  onApply,
  onCancel,
}: PageConfigDialogProps) => {
  const [localPage, setLocalPage] =
    useState<VersionReportVersionPageFormModel | null>(null)

  const [showErrors, setShowErrors] = useState(false)
  const [errorPopoverOpen, setErrorPopoverOpen] = useState(false)
  const [errorBadgeEl, setErrorBadgeEl] = useState<HTMLButtonElement | null>(
    null
  )

  useEffect(() => {
    if (open && page) {
      setLocalPage(structuredClone(page))
      setShowErrors(false)
      setErrorPopoverOpen(false)
    }
  }, [open, page])

  const handleLocalUpdate = useCallback(
    (
      _pageNumber: number,
      updater: (
        prev: VersionReportVersionPageFormModel
      ) => VersionReportVersionPageFormModel
    ) => {
      setLocalPage(prev => (prev ? updater(prev) : null))
      setShowErrors(true)
    },
    []
  )

  const localValidationErrors: PageValidationError[] = useMemo(() => {
    if (!localPage) return []
    return validatePageEntity(toEntityForValidation(localPage))
  }, [localPage])

  const handleApply = useCallback(() => {
    if (!localPage) return
    if (localValidationErrors.length > 0) {
      setShowErrors(true)
      setErrorPopoverOpen(true)
      return
    }
    onApply(localPage)
  }, [localPage, localValidationErrors, onApply])

  if (!open || !localPage) {
    return null
  }

  const hasVisibleErrors = showErrors && localValidationErrors.length > 0

  const typeLabel = PAGE_TYPE_LABELS[localPage.pageType]
  const typeColor = PAGE_TYPE_COLORS[localPage.pageType]

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth={true}
      PaperProps={{
        style: {
          borderRadius: '12px',
          maxHeight: '90%',
        },
      }}
    >
      <Header>
        <HeaderLeft>
          <TypeDot
            color={hasVisibleErrors ? colorPalette.pink[5] : typeColor}
          />
          <HeaderTitle>ページ設定</HeaderTitle>
          <HeaderSeparator>·</HeaderSeparator>
          <HeaderTypeLabel>{typeLabel}</HeaderTypeLabel>
          {hasVisibleErrors && (
            <ErrorBadge
              ref={setErrorBadgeEl}
              type="button"
              onClick={() => setErrorPopoverOpen(o => !o)}
            >
              <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
              {localValidationErrors.length} 件の入力エラー
            </ErrorBadge>
          )}
        </HeaderLeft>
        <CloseButton onClick={onCancel} aria-label="閉じる">
          <CloseRoundedIcon fontSize="small" />
        </CloseButton>
      </Header>
      <Popover
        open={errorPopoverOpen && Boolean(errorBadgeEl)}
        anchorEl={errorBadgeEl}
        onClose={() => setErrorPopoverOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <ErrorPopoverContent>
          {localValidationErrors.map((error, index) => (
            <ErrorPopoverItem key={index}>• {error.message}</ErrorPopoverItem>
          ))}
        </ErrorPopoverContent>
      </Popover>
      <StyledDialogContent>
        <PageConfigForm
          page={localPage}
          snapshotUuid={snapshotUuid}
          onUpdate={handleLocalUpdate}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <Button colorPattern="monotone" variant="outlined" onClick={onCancel}>
          キャンセル
        </Button>
        <Button colorPattern="skyBlue" onClick={handleApply}>
          適用
        </Button>
      </StyledDialogActions>
    </Dialog>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 20px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const HeaderLeft = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const TypeDot = styled('span')<{ color: string }>(({ color }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: color,
  flexShrink: 0,
}))

const HeaderTitle = styled('span')({
  fontSize: '15px',
  fontWeight: 'bold',
  color: colorPalette.monotone[10],
})

const HeaderSeparator = styled('span')({
  color: colorPalette.monotone[4],
})

const ErrorBadge = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  marginLeft: '8px',
  padding: '2px 10px',
  border: `1px solid ${colorPalette.pink[5]}`,
  borderRadius: '999px',
  backgroundColor: colorPalette.pink[0],
  color: colorPalette.pink[5],
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1.6,
  cursor: 'pointer',
})

const ErrorPopoverContent = styled('div')({
  padding: '10px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  maxWidth: '320px',
})

const ErrorPopoverItem = styled('div')({
  fontSize: '13px',
  color: colorPalette.pink[5],
})

const HeaderTypeLabel = styled('span')({
  fontSize: '13px',
  color: colorPalette.monotone[6],
})

const CloseButton = styled(IconButton)({
  color: colorPalette.monotone[4],
  padding: '4px',
})

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  minHeight: '400px',
})

const StyledDialogActions = styled(DialogActions)({
  padding: '12px 20px',
  gap: '8px',
})
